/**
 * @fileoverview Delta Base Observability Framework
 *
 * This module provides a comprehensive observability framework built on top of OpenTelemetry
 * with semantic wide events, automatic middleware, and utility functions for common patterns.
 *
 * @example
 * ```typescript
 * import { observabilityMiddleware, WideEventsWrapper, withDomainTracking, configureObservability } from '@delta-base/observability';
 *
 * // Basic setup with middleware
 * app.use('*', observabilityMiddleware());
 *
 * // Configure observability globally
 * configureObservability({
 *   serviceName: 'my-service',
 *   autoTrackHttp: true,
 *   debug: true
 * });
 *
 * // In route handlers
 * app.post('/users', async (c) => {
 *   const tracker = c.get('wideEvent');
 *   tracker.trackUser({ email: 'user@example.com' });
 * });
 * ```
 */

// Import for internal use in default export
import {
  configureObservability,
  configureObservabilityAdvanced,
  observabilityConfig,
} from './config';
import {
  addContextFields,
  addErrorPattern,
  type ObservabilityOptions,
  observabilityMiddleware,
} from './middleware';
import {
  withDomainTracking,
  withEventStoreTracking,
  withValidationTracking,
} from './utils';
import {
  createOperationTracker,
  getBuildInfo,
  initializeBuildInfo,
  setBuildInfo,
  WideEventsWrapper,
} from './wide-events';

// Configuration system
export {
  type ContextSchemaConfig,
  configureObservability,
  configureObservabilityAdvanced,
  createObservabilityConfig,
  DEFAULT_CONTEXT_SCHEMA,
  DEFAULT_ERROR_PATTERNS,
  DEFAULT_OBSERVABILITY_CONFIG,
  type ErrorResponsePattern,
  type ErrorTrackingConfig,
  type HttpTrackingConfig,
  type ObservabilityConfig,
  ObservabilityConfigManager,
  observabilityConfig,
  type PerformanceConfig,
  type TracingConfig,
} from './config';
// Error handling utilities
export {
  categorizeError,
  createErrorGuard,
  DEFAULT_EXPECTED_ERROR_GUARDS,
  type ErrorCategorizationConfig,
  handleAndTrackError,
  isExpectedError,
} from './error-handling';
// Middleware for automatic setup
export {
  addContextFields,
  addErrorPattern,
  createObservabilityErrorHandler,
  createObservabilityMiddleware,
  extractUserContext,
  type ObservabilityOptions,
  observabilityMiddleware,
  trackOperationTiming,
} from './middleware';
export type { BuildInfoOptions } from './scripts';
// Build info utilities - NOTE: collectBuildInfo is only available as CLI tool, not as import
// Low-level tracing utilities (for advanced use cases)
export {
  createSpan,
  initializeTracing,
  type TraceOptions,
  withSpan,
} from './tracing';
// Hono types for extending in consuming services
export type {
  BaseHonoBindings,
  BaseHonoVariables,
  ExtendHonoBindings,
  ExtendHonoVariables,
} from './types/hono';
// Utility functions for common patterns
export {
  createSimpleTracker,
  trackUserFromSources,
  withDatabaseTracking,
  withDomainTracking,
  withEventStoreTracking,
  withExternalServiceTracking,
  withValidationTracking,
} from './utils';
// Core observability classes and types
export {
  createOperationTracker,
  createUserOperationTracker,
  DEFAULT_BUILD_INFO,
  type EntityData,
  type ErrorData,
  getBuildInfo,
  type InfrastructureData,
  initializeBuildInfo,
  type OperationData,
  setBuildInfo,
  type UserData,
  WideEventsWrapper,
} from './wide-events';

/**
 * Quick start function for new services.
 *
 * This function provides a one-line setup for basic observability in new services.
 *
 * @param serviceName - Name of your service
 * @param options - Optional configuration options
 * @returns Configured observability middleware
 *
 * @example
 * ```typescript
 * import { quickStart } from '@delta-base/observability';
 *
 * const app = new OpenAPIHono();
 * app.use('*', quickStart('my-service'));
 *
 * // With additional options
 * app.use('*', quickStart('my-service', { debug: true }));
 * ```
 */
export function quickStart(
  serviceName: string,
  options: Omit<ObservabilityOptions, 'serviceName'> = {}
) {
  return observabilityMiddleware({ serviceName, ...options });
}

/**
 * Default export for convenient importing
 */
export default {
  // Core functionality
  middleware: observabilityMiddleware,
  WideEventsWrapper,
  createOperationTracker,
  quickStart,

  // Configuration
  configure: configureObservability,
  configureAdvanced: configureObservabilityAdvanced,
  config: observabilityConfig,
  addErrorPattern: addErrorPattern,
  addContextFields: addContextFields,

  // Utility functions
  withDomainTracking,
  withEventStoreTracking,
  withValidationTracking,

  // Build info
  initializeBuildInfo,
  setBuildInfo,
  getBuildInfo,
  // NOTE: collectBuildInfo is only available as CLI tool, not as import
};
